import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../hooks/useToast';
import axios from 'axios';
import { extractPrimaryImage } from '../utils/imageHelpers';

const SellerOffersDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedStatus, setSelectedStatus] = useState('pending');
  const [stats, setStats] = useState({});
  const [respondingToOffer, setRespondingToOffer] = useState(null);
  const [responseMessage, setResponseMessage] = useState('');
  const [showFeePopup, setShowFeePopup] = useState(false);
  const [selectedOfferForAcceptance, setSelectedOfferForAcceptance] = useState(null);
  const [acceptDurationHours, setAcceptDurationHours] = useState('48');
  const [acceptInfinite, setAcceptInfinite] = useState(false);

  // Fetch offers received by seller
  const fetchOffers = async (page = 1, status = 'pending') => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '10'
      });
      
      if (status && status !== 'all') {
        params.append('status', status);
      }

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/second-hand-offers/received?${params}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        setOffers(response.data.data);
        setTotalPages(response.data.pagination.totalPages || 1);
        setCurrentPage(response.data.pagination.page || 1);
      }
    } catch (error) {
      console.error('Error fetching offers:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch offer statistics
  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/second-hand-offers/stats`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        setStats(response.data.data.user || {});
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  useEffect(() => {
    fetchOffers(1, selectedStatus);
    fetchStats();
  }, [selectedStatus]);

  const handleStatusFilter = (status) => {
    setSelectedStatus(status);
    setCurrentPage(1);
    fetchOffers(1, status);
  };

  const handlePageChange = (page) => {
    fetchOffers(page, selectedStatus);
  };

  const handleRespondToOffer = async (offerId, action) => {
    try {
      setRespondingToOffer(offerId);
      const token = localStorage.getItem('token');
      const payload = { action, sellerResponse: responseMessage };
      if (action === 'accept') {
        payload.durationHours = acceptInfinite ? undefined : (acceptDurationHours ? parseInt(acceptDurationHours, 10) : 48);
        payload.isInfinite = acceptInfinite || undefined;
      }
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/api/second-hand-offers/${offerId}/respond`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        toast.success(`Offer ${action}ed successfully!`);
        fetchOffers(currentPage, selectedStatus);
        fetchStats();
        setResponseMessage('');
      } else {
        toast.error(response.data.message || `Failed to ${action} offer`);
      }
    } catch (error) {
      console.error(`Error ${action}ing offer:`, error);
      toast.error(error.response?.data?.message || `Failed to ${action} offer`);
    } finally {
      setRespondingToOffer(null);
    }
  };

  const formatPrice = (price) => `${new Intl.NumberFormat('en-US').format(price ?? 0)} TND`;

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'text-yellow-400 bg-yellow-400/10',
      accepted: 'text-green-400 bg-green-400/10',
      rejected: 'text-red-400 bg-red-400/10',
      expired: 'text-gray-400 bg-gray-400/10',
      withdrawn: 'text-gray-400 bg-gray-400/10'
    };
    return colors[status] || 'text-gray-400 bg-gray-400/10';
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: '⏳',
      accepted: '✅',
      rejected: '❌',
      expired: '⏰',
      withdrawn: '🚫'
    };
    return icons[status] || '❓';
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Please Login</h2>
          <p className="text-gray-400">You need to be logged in to access the seller dashboard.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 py-8">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">📥 Offers Received</h1>
          <p className="text-gray-400">Manage offers on your second-hand products</p>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <div className="text-2xl text-blue-400 mb-2">📬</div>
            <div className="text-2xl font-bold text-white">{stats.offersReceived || 0}</div>
            <div className="text-gray-400 text-sm">Total Received</div>
          </div>
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <div className="text-2xl text-yellow-400 mb-2">⏳</div>
            <div className="text-2xl font-bold text-white">{stats.pendingReceived || 0}</div>
            <div className="text-gray-400 text-sm">Pending</div>
          </div>
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <div className="text-2xl text-orange-400 mb-2">📤</div>
            <div className="text-2xl font-bold text-white">{stats.offersMade || 0}</div>
            <div className="text-gray-400 text-sm">Made by You</div>
          </div>
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <div className="text-2xl text-purple-400 mb-2">🎯</div>
            <div className="text-2xl font-bold text-white">{stats.pendingMade || 0}</div>
            <div className="text-gray-400 text-sm">Your Pending</div>
          </div>
        </div>

        {/* Status Filter */}
        <div className="bg-gray-800 rounded-lg p-4 mb-6 border border-gray-700">
          <div className="flex flex-wrap gap-2">
            {['pending', 'accepted', 'rejected', 'expired', 'all'].map((status) => (
              <button
                key={status}
                onClick={() => handleStatusFilter(status)}
                className={`px-4 py-2 rounded-lg transition-all duration-300 ${
                  selectedStatus === status
                    ? 'bg-yellow-500 text-black font-medium'
                    : 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                }`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Offers List */}
        {loading ? (
          <div className="bg-gray-800 rounded-lg p-8 text-center border border-gray-700">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-400 mx-auto mb-4"></div>
            <p className="text-gray-400">Loading offers...</p>
          </div>
        ) : offers.length > 0 ? (
          <div className="space-y-4">
            {offers.map((offer) => (
              <div key={offer._id} className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                  {/* Offer Info */}
                  <div className="flex-1 mb-4 lg:mb-0">
                    <div className="flex items-start space-x-4">
                      {/* Product Image */}
                      <div className="w-16 h-16 bg-gray-700 rounded-lg flex items-center justify-center overflow-hidden">
                        {(() => {
                          const { url, thumbnailUrl } = extractPrimaryImage(offer.productId);
                          const imageSrc = thumbnailUrl || url;
                          return imageSrc ? (
                            <img
                              src={imageSrc}
                              alt={offer.productId?.name || 'Product image'}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <span className="text-2xl">📦</span>
                          );
                        })()}
                      </div>
                      
                      {/* Details */}
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <h3 className="text-lg font-semibold text-white">
                            {offer.productId?.name || 'Product'}
                          </h3>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(offer.status)}`}>
                            {getStatusIcon(offer.status)} {offer.status.toUpperCase()}
                          </span>
                        </div>
                        
                        <div className="space-y-1 text-sm text-gray-400">
                          <p>
                            <span className="font-medium">Buyer:</span> {offer.buyerId?.username || 'Anonymous'}
                          </p>
                          <p>
                            <span className="font-medium">Offer Amount:</span> 
                            <span className="text-yellow-400 font-bold ml-1">
                              {formatPrice(offer.offerAmount)}
                            </span>
                          </p>
                          <p>
                            <span className="font-medium">Reserve Price:</span> {formatPrice(offer.productId?.reservePrice || 0)}
                          </p>
                          <p>
                            <span className="font-medium">Date:</span> {formatDate(offer.createdAt)}
                          </p>
                          {offer.message && (
                            <p>
                              <span className="font-medium">Message:</span> 
                              <span className="text-gray-300 italic ml-1">"{offer.message}"</span>
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="lg:ml-6">
                    {offer.status === 'pending' && (
                      <div>
                        <div className="mb-3">
                          <textarea
                            value={responseMessage}
                            onChange={(e) => setResponseMessage(e.target.value)}
                            placeholder="Optional response message..."
                            rows="2"
                            maxLength="500"
                            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-400 text-sm"
                          />
                        </div>
                        <div className="flex space-x-2 mb-3">
                          <button
                            onClick={() => {
                              setSelectedOfferForAcceptance(offer);
                              // Prefill with product defaults if available
                              const prod = offer.productId || {};
                              if (prod.defaultOfferInfinite) {
                                setAcceptInfinite(true);
                              } else {
                                setAcceptInfinite(false);
                                if (typeof prod.defaultOfferDurationHours !== 'undefined') {
                                  setAcceptDurationHours(String(prod.defaultOfferDurationHours || 48));
                                } else {
                                  setAcceptDurationHours('48');
                                }
                              }
                              setShowFeePopup(true);
                            }}
                            disabled={respondingToOffer === offer._id}
                            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors duration-300 disabled:opacity-50 text-sm"
                          >
                            {respondingToOffer === offer._id ? 'Processing...' : '✅ Accept'}
                          </button>
                          <button
                            onClick={() => handleRespondToOffer(offer._id, 'reject')}
                            disabled={respondingToOffer === offer._id}
                            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors duration-300 disabled:opacity-50 text-sm"
                          >
                            {respondingToOffer === offer._id ? 'Processing...' : '❌ Reject'}
                          </button>
                        </div>
                      </div>
                    )}
                    
                    {/* View Details Link */}
                    <button
                      onClick={() => navigate(`/offers/${offer._id}`)}
                      className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-300 text-sm"
                    >
                      👁️ View Details
                    </button>
                  </div>
                  
                  {/* Response from seller (for non-pending offers) */}
                  {offer.status !== 'pending' && offer.sellerResponse && (
                    <div className="lg:ml-6 mt-4 lg:mt-0">
                      <div className="bg-gray-700 rounded-lg p-3 max-w-xs">
                        <p className="text-xs text-gray-400 mb-1">Your response:</p>
                        <p className="text-sm text-white italic">"{offer.sellerResponse}"</p>
                        <p className="text-xs text-gray-400 mt-1">
                          {formatDate(offer.respondedAt)}
                        </p>
                        {(offer.status === 'pending' && offer.provisionalAccepted) && (
                          <p className="text-xs text-yellow-400 mt-2">Waiting for buyer fee to finalize.</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-gray-800 rounded-lg p-8 text-center border border-gray-700">
            <div className="text-6xl mb-4">📭</div>
            <h3 className="text-xl font-semibold text-white mb-2">No Offers Found</h3>
            <p className="text-gray-400">
              {selectedStatus === 'pending' 
                ? "You don't have any pending offers at the moment."
                : `No ${selectedStatus} offers found.`
              }
            </p>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center space-x-4 mt-8">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-300"
            >
              ← Previous
            </button>
            
            <div className="flex space-x-2">
              {[...Array(totalPages)].map((_, i) => (
                <button
                  key={i + 1}
                  onClick={() => handlePageChange(i + 1)}
                  className={`w-10 h-10 rounded-lg font-medium transition-colors duration-300 ${
                    currentPage === i + 1
                      ? 'bg-yellow-500 text-black'
                      : 'bg-gray-700 hover:bg-gray-600 text-white'
                  }`}
                >
                  {i + 1}
                </button>
              ))}
            </div>
            
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-300"
            >
              Next →
            </button>
          </div>
        )}
      </div>

      {/* Fee Confirmation Popup */}
      {showFeePopup && selectedOfferForAcceptance && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 border border-gray-700">
            <div className="text-center mb-6">
              <div className="text-4xl mb-4">💰</div>
              <h3 className="text-xl font-bold text-white mb-2">Accept Offer & Enable Chat</h3>
              <p className="text-gray-400 text-sm">
                By accepting this offer, the buyer will be required to pay a fee to unlock chat functionality.
              </p>
            </div>

            <div className="bg-gray-700 rounded-lg p-4 mb-6">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">Product:</span>
                  <span className="text-white font-medium">{selectedOfferForAcceptance.productId?.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Offer Amount:</span>
                  <span className="text-yellow-400 font-bold">{formatPrice(selectedOfferForAcceptance.offerAmount)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Chat Fee:</span>
                  <span className="text-green-400 font-bold">{formatPrice(selectedOfferForAcceptance.feeAmount || 1)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Buyer:</span>
                  <span className="text-white">{selectedOfferForAcceptance.buyerId?.username}</span>
                </div>
              </div>
            </div>

            <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-3 mb-6">
              <p className="text-blue-300 text-sm">
                <strong>Note:</strong> Once accepted, the buyer must pay the chat fee before they can contact you. 
                This ensures serious buyers only and helps prevent spam.
              </p>
            </div>
            <div className="mb-6">
              <label className="block text-gray-300 text-sm font-medium mb-2">Offer Visibility Duration</label>
              <div className="space-y-3">
                <button
                  type="button"
                  onClick={() => setAcceptInfinite(true)}
                  className={`w-full text-left flex items-start space-x-3 p-3 rounded-lg border transition-colors duration-200 ${acceptInfinite ? 'bg-gray-700/70 border-green-500/60 ring-2 ring-green-500/40' : 'bg-gray-800 border-gray-700 hover:bg-gray-700'}`}
                  aria-pressed={acceptInfinite}
                >
                  <span className={`mt-1 inline-flex h-4 w-4 rounded-full border-2 ${acceptInfinite ? 'border-green-400 bg-green-500' : 'border-gray-500'}`}></span>
                  <div>
                    <p className="text-white font-medium">Forever (No Expiration)</p>
                    <p className="text-xs text-gray-400 mt-0.5">Offer stays active until manually withdrawn or finalized.</p>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setAcceptInfinite(false)}
                  className={`w-full text-left flex items-start space-x-3 p-3 rounded-lg border transition-colors duration-200 ${!acceptInfinite ? 'bg-gray-700/70 border-blue-500/60 ring-2 ring-blue-500/40' : 'bg-gray-800 border-gray-700 hover:bg-gray-700'}`}
                  aria-pressed={!acceptInfinite}
                >
                  <span className={`mt-1 inline-flex h-4 w-4 rounded-full border-2 ${!acceptInfinite ? 'border-blue-400 bg-blue-500' : 'border-gray-500'}`}></span>
                  <div className="flex-1">
                    <p className="text-white font-medium">Countdown</p>
                    <p className="text-xs text-gray-400 mt-0.5">Expires after the selected number of hours.</p>
                    <div className="mt-2 flex items-center space-x-2">
                      <input
                        type="number"
                        min="1"
                        max="720"
                        disabled={acceptInfinite}
                        value={acceptDurationHours}
                        onChange={(e)=>setAcceptDurationHours(e.target.value)}
                        className="w-28 px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-green-400 text-sm"
                        placeholder="48"
                      />
                      <span className="text-xs text-gray-400">hours (max 720)</span>
                    </div>
                  </div>
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">Choose either Forever or a finite countdown. Default 48h if left blank.</p>
              {!acceptInfinite && acceptDurationHours && parseInt(acceptDurationHours,10) > 720 && (
                <p className="text-xs text-red-400 mt-1">Maximum allowed is 720 hours (30 days).</p>
              )}
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowFeePopup(false);
                  setSelectedOfferForAcceptance(null);
                  setResponseMessage('');
                }}
                className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors duration-300"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  handleRespondToOffer(selectedOfferForAcceptance._id, 'accept');
                  setShowFeePopup(false);
                  setSelectedOfferForAcceptance(null);
                }}
                disabled={respondingToOffer === selectedOfferForAcceptance._id}
                className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors duration-300 disabled:opacity-50"
              >
                {respondingToOffer === selectedOfferForAcceptance._id ? 'Accepting...' : 'Accept Offer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SellerOffersDashboard;
